import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Compass, Sparkles, Search, Leaf, Bookmark, Utensils, Globe2,
  Salad, Brain, CheckCircle2, ArrowRight,
} from "lucide-react";

const GUIDE_KEY = "yumo_learn_completed";

const LESSONS = [
  {
    icon: Compass,
    title: "Discover recipes",
    body: "Open Mother Drive to get personalised picks in ~10 seconds — tuned to your mood, weather, and taste DNA.",
    to: "/recommend",
    cta: "Open Mother Drive",
  },
  {
    icon: Sparkles,
    title: "Ask YUMO AI",
    body: "Tap the YumoPot bubble anywhere. Ask 'What should I eat right now?', 'Suggest breakfast in Mysore', or 'Give me a paneer dinner idea'.",
    to: null,
  },
  {
    icon: Search,
    title: "Search by ingredients",
    body: "Type what's in your fridge inside Ask YUMO. It'll spin up a full recipe with steps, tips and pairings.",
    to: null,
  },
  {
    icon: Salad,
    title: "Find healthy alternatives",
    body: "Ask 'lighter version of biryani' or 'low-oil breakfast'. YUMO suggests healthier swaps grounded in real cuisine.",
    to: null,
  },
  {
    icon: Bookmark,
    title: "Save favourites",
    body: "Every 'Love it' feedback trains your Taste DNA. Recently viewed and liked dishes appear in your Dashboard journey.",
    to: "/dashboard",
    cta: "Go to Dashboard",
  },
  {
    icon: Utensils,
    title: "Build meal plans",
    body: "Combine Discover + Explore to plan breakfast, lunch, and dinner. Filter by cuisine, prep-time, or diet.",
    to: "/explorer",
    cta: "Browse Explorer",
  },
  {
    icon: Globe2,
    title: "Browse cuisines",
    body: "The Food Universe covers regional Indian and global cuisines. Toggle filters to explore Andhra, Karnataka, Kerala, Italian and more.",
    to: "/explorer",
  },
  {
    icon: Leaf,
    title: "Dietary filters",
    body: "Set your diet as Vegetarian, Vegan, Eggitarian, Jain, or Non-veg in Profile. YUMO strictly respects it — meat is never shown to vegetarians.",
    to: "/profile",
    cta: "Update profile",
  },
  {
    icon: Brain,
    title: "How AI recommendations work",
    body: "Mother Drive fuses six signals — Identity, Region, Mood, Comfort, Weather, Diet — and explains why each dish was picked. Confidence score is visible on every pick.",
    to: null,
  },
];

export default function LearnYumo() {
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setCompleted(localStorage.getItem(GUIDE_KEY) === "1");
    }
  }, []);

  const markComplete = () => {
    if (typeof window !== "undefined") localStorage.setItem(GUIDE_KEY, "1");
    setCompleted(true);
  };

  const resetGuide = () => {
    if (typeof window !== "undefined") localStorage.removeItem(GUIDE_KEY);
    setCompleted(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 pb-16" data-testid="learn-yumo-page">
      <div className="max-w-3xl">
        <div className="overline mb-2">Learn YUMO</div>
        <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.02]">
          How to use YUMO — <span className="text-muted-foreground italic">in 9 short lessons.</span>
        </h1>
        <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
          A quick beginner-friendly walkthrough of every core feature. Come back anytime from the More menu.
        </p>
        {completed ? (
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-emerald-600" data-testid="learn-complete-badge">
            <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
            You&rsquo;ve completed the guide — <button onClick={resetGuide} className="underline underline-offset-4" data-testid="learn-reset-btn">reset progress</button>
          </div>
        ) : (
          <div className="mt-6">
            <Button onClick={markComplete} className="rounded-full" data-testid="learn-complete-btn">
              Mark guide as done <CheckCircle2 className="ml-1 w-4 h-4" strokeWidth={1.75} />
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
        {LESSONS.map((l, i) => (
          <Card key={l.title} className="p-6 rounded-2xl border-border shadow-none hover:shadow-sm transition" data-testid={`learn-card-${i}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <l.icon className="w-4 h-4 text-primary" strokeWidth={1.75} />
              </div>
              <div className="overline">Lesson {String(i + 1).padStart(2, "0")}</div>
            </div>
            <h3 className="font-serif text-xl font-semibold tracking-tight mb-2">{l.title}</h3>
            <p className="text-sm text-foreground/80 leading-relaxed">{l.body}</p>
            {l.to && (
              <Link to={l.to} className="mt-4 inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid={`learn-cta-${i}`}>
                {l.cta || "Try it now"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
